import { Alignment } from "@mcleod/core";
import { ImageName } from "@mcleod/images";
import { Button, ButtonProps, ClickEvent, Image } from "@mcleod/components";
import { AbstractComponentDemoExamples } from "./AbstractComponentDemoExamples";
import { LiveExampleComponents } from "../LiveExampleComponents";

export class DemoButtonExamples extends AbstractComponentDemoExamples {
    protected override registerExamples() {
        super.registerExamples();
        this.registerPropertyExamples("busy", {
            examples: [
                { value: true, description: "A button that is busy is disabled and displays the rotating spinner icon (commonly called the busy spinner).  The spinner displays to the left as that is the default value for the imageAlign property." },
                { value: true, props: { imageAlign: Alignment.RIGHT }, description: "A button that is busy, where the rotating spinner icon is right-aligned." },
                {
                    value: false,
                    props: {
                        imageName: ImageName.add,
                        onClick: (event: ClickEvent) => {
                            const button = event.target as Button;
                            button.busy = true;
                            setTimeout(() => button.busy = false, 5000);
                        }
                    },
                    onCreate: (exampleComponents: LiveExampleComponents) => {
                        const buttonDisable = new Button({
                            caption: "Disable Button",
                            width: 200,
                            backgroundColor: "primary",
                            color: "primary.reverse",
                            marginLeft: 15,
                            rowBreak: false,
                            onClick: () => {
                                const exampleButton = exampleComponents.liveComponent as Button;
                                exampleButton.enabled = !exampleButton.enabled;
                                buttonDisable.caption = exampleButton.enabled ? "Disable Button" : "Enable Button";
                            }
                        });
                        const buttonMakeBusy = new Button({
                            caption: "Make Example Button Busy",
                            width: 200,
                            backgroundColor: "primary",
                            color: "primary.reverse",
                            marginLeft: 15,
                            rowBreak: false,
                            onClick: () => {
                                const exampleButton = exampleComponents.liveComponent as Button;
                                exampleButton.busy = true;
                                buttonMakeBusy.enabled = false;
                                setTimeout(() => {
                                    exampleButton.busy = false;
                                    buttonMakeBusy.enabled = true;
                                }, 5000);
                            }
                        });
                        exampleComponents.liveComponent.rowBreak = false;
                        exampleComponents.livePanel.add(buttonDisable, buttonMakeBusy);
                    },
                    description: `A button that becomes busy will restore its pre-busy values for both the enabled and imageName properties. This can be demonstrated below.

For the enabled property, the additional buttons below can be used to make the example button disabled and then make the button busy, even while it is disabled. This demonstrates that the button will stay disabled after it is no longer busy.

For the imageName property, the button will restore its original image when the button is no longer busy.`
                },
            ]
        });
        this.registerPropertyExamples("caption", {
            examples: [
                { value: "Click me!" },
                {
                    description: "Unless a button has a specified width, it will grow to the size of caption (plus padding and an image, if set)",
                    value: "Some other caption to demonstrate what changing it does"
                }
            ]
        });
        this.registerPropertyExamples("default", {
            examples: [
                { value: true },
                { value: false },
            ]
        });
        this.registerPropertyExamples("focusable", {
            examples: [
                { value: true, description: "Note how you can tab into this Button and that it maintains focus after clicking." },
                { value: false, description: "And this Button doesn't allow focus." },
            ]
        });
        this.registerPropertyExamples("imageAlign", {
            props: { imageName: ImageName.circleCheck },
            examples: [
                { value: Alignment.LEFT, description: "The image is to the left of the Button's text." },
                { value: Alignment.TOP, description: "The image is centered on top of the Button's text." },
                { value: Alignment.BOTTOM, description: "The image is centered underneath the Button's text." },
                { value: Alignment.RIGHT, description: "The image is to the right of the Button's text." },
            ]
        });
        this.registerPropertyExamples("imageColor", {
            props: { imageName: ImageName.circleCheck },
            examples: [
                { value: "primary", description: "Typically you would use colors available in the user's theme.  (provide link to theme documentation)" },
                { value: "red", description: "When necessary, you can also use colors by name." },
                { value: "#612965", description: "Or any other valid HTML color notation, like hex values.  (provide link to color documentation)" },
            ]
        });
        this.registerPropertyExamples("imageTransition", {
            props: { imageName: ImageName.activateSlideout },
            examples: [
                {
                    value: Image.resolveTransitionValue(2000),
                    description: "The 'activateSlideout' image is displayed within the example 'Click me!' button below.  Click the 'Rotate 45 Degrees' button to rotate the 'Click me!' button's image 45 degrees (clockwise).  Because the transition property sets any 'transform' change to take 2 seconds, the change in rotation will take 2 seconds to complete.  The 'ease-in-out' property controls the style of the transition."
                }
            ],
            onCreate: (exampleComponents: LiveExampleComponents) => {
                const button = new Button({
                    caption: "Rotate 45 Degrees",
                    width: 200,
                    backgroundColor: "primary",
                    color: "primary.reverse",
                    marginLeft: 15,
                    onClick: () => {
                        const exampleButton = exampleComponents.liveComponent as Button;
                        exampleButton.imageRotation = (exampleButton.imageRotation ?? 0) + 45;
                    }
                });
                exampleComponents.liveComponent.rowBreak = false;
                exampleComponents.livePanel.add(button);
            }
        });
        this.registerPropertyExamples("variant", {
            props: { imageName: ImageName.circleCheck },
            examples: [
                { value: undefined, description: "Not setting a variant yields a rectangular button with a 1px border of the current color, unless the border is changed specifically." },
                { value: "round", props: { caption: undefined }, description: "The \"round\" variant is a shortcut to remove the border, adjust padding, and make the Button round.  It is more often used for Buttons that contain just an image." },
            ]
        });
    }

    protected override get propsForAllExamples(): Partial<ButtonProps> {
        return {
            ...super.propsForAllExamples,
            caption: "Click me!"
        };
    }
}
